import React from 'react';
import { Tab, Card, Table, Badge, Button, Dropdown, DropdownButton, Form } from 'react-bootstrap';
import { ChevronUp, ChevronDown } from 'lucide-react';

/** Five inline checklist review Tab.Panes (licenses through scholarly) for Promotion Manager. */
export default function PromotionEvidenceReviewTabPanes({
  evidenceReviewRowsLicenses,
  evidenceReviewRowsOnboarding,
  evidenceReviewRowsTraining,
  evidenceReviewRowsVaccinations,
  evidenceReviewRowsScholarly,
  selectedAcademicYear,
  setSelectedAcademicYear,
  vaccinationSubmissionsExpandedIds,
  toggleVaccinationSubmissionsPanel,
  buildVaccinationDisplayEntries,
  renderEvidenceExpirationCell,
  renderEvidenceSubmittedDetailsCell,
  openProtectedEvidenceFile,
  setVaccinationCardViewItem,
  setConfirmMessage,
  setConfirmAction,
  setShowConfirmModal,
  reviewOnboardingDocumentSubmission,
  openDeleteEvidenceConfirm,
  reviewEvidence,
  requestRejectEvidence,
}) {
  const academicYearOptions = [
    selectedAcademicYear - 2,
    selectedAcademicYear - 1,
    selectedAcademicYear,
    selectedAcademicYear + 1,
    selectedAcademicYear + 2
  ].filter((year, idx, arr) => arr.indexOf(year) === idx);

  const renderChecklistHeader = (title) => (
    <Card.Header className="px-4 py-3 border-0 d-flex align-items-center justify-content-between gap-2">
      <h6 className="mb-0 fw-semibold">{title}</h6>
      <Form.Select
        value={selectedAcademicYear}
        size="sm"
        style={{ maxWidth: 180 }}
        onChange={(e) => setSelectedAcademicYear(parseInt(e.target.value, 10))}
      >
        {academicYearOptions.map((year) => (
          <option key={year} value={year}>
            {year}-{year + 1}
          </option>
        ))}
      </Form.Select>
    </Card.Header>
  );

  return (
    <>
              {/* Licenses and Certifications Review */}
              <Tab.Pane eventKey="licenses" key="licenses">
                    <Card className="shadow-sm border-0 mb-3" style={{ height: '100%', minHeight: '425px' }}>
                      {renderChecklistHeader('Licenses and Certifications Review')}
                      <Card.Body className={evidenceReviewRowsLicenses.length === 0 ? 'p-4' : 'p-0'}>
                        {evidenceReviewRowsLicenses.length === 0 ? (
                          <div className="text-center py-5 px-3 text-muted">
                            <i className="bi bi-inbox-fill mb-3" style={{ fontSize: '2.75rem' }} />
                            <div className="fw-semibold text-muted mb-1">Nothing to review yet</div>
                            <p className="small mb-0 mx-auto" style={{ maxWidth: 420 }}>
                              When residents submit licenses and certifications for your program, they will appear here for approval.
                            </p>
                          </div>
                        ) : (
                          <Table responsive className="mb-0">
                            <thead>
                              <tr className="custom-header-bg">
                                <th className="px-4">Resident</th>
                                <th>Item</th>
                                <th>Expiration</th>
                                <th>Submitted details</th>
                                <th>Status</th>
                                <th>Evidence</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {evidenceReviewRowsLicenses.map((item) => (
                                <tr key={item.id}>
                                  <td className="px-4">
                                    {item.resident?.firstName} {item.resident?.lastName}
                                  </td>
                                  <td>
                                    <div className="d-flex flex-column gap-1">
                                      <span>{item.itemName}</span>
                                    </div>
                                  </td>
                                <td className="small text-nowrap">{renderEvidenceExpirationCell('licensesCertifications', item)}</td>
                                  <td>
                                    {item.isOnboardingBridge && item.bridgeKind === 'package_assignment' ? (
                                      <span className="small text-muted">Structured package — open in Onboarding to review sections.</span>
                                    ) : item.category === 'vaccinationCard' ? null : (
                                      renderEvidenceSubmittedDetailsCell('licensesCertifications', item)
                                    )}
                                  </td>
                                  <td>
                                    <Badge
                                      bg={
                                        item.status === 'approved'
                                          ? 'success'
                                          : item.status === 'rejected'
                                            ? 'danger'
                                            : 'warning'
                                      }
                                    >
                                      {item.status}
                                    </Badge>
                                  </td>
                                  <td>
                                    {item.evidencePath ? (
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => openProtectedEvidenceFile(item.evidencePath)}
                                      >
                                        View
                                      </Button>
                                    ) : item.category === 'vaccinationCard' ? (
                                      <Button
                                        size="sm"
                                        variant="primary"
                                        onClick={() => setVaccinationCardViewItem(item)}
                                      >
                                        <i className="bi bi-card-checklist me-1" />
                                        View vaccination card
                                      </Button>
                                    ) : item.isOnboardingBridge && item.bridgeKind === 'package_assignment' ? (
                                      <span className="small text-muted">—</span>
                                    ) : (
                                      'N/A'
                                    )}
                                  </td>
                                  <td>
                                    {item.isOnboardingBridge && item.bridgeKind === 'package_assignment' ? (
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => {
                                          const rid = item.resident?.id;
                                          window.location.href = rid
                                            ? `/onboarding#tab=submissions&resident=${rid}`
                                            : '/onboarding#tab=submissions';
                                        }}
                                      >
                                        Open in Onboarding
                                      </Button>
                                    ) : item.isOnboardingBridge && item.bridgeKind === 'document_submission' ? (
                                      item.status === 'approved' ? (
                                        <Button
                                          size="sm"
                                          variant="outline-danger"
                                          onClick={() => {
                                            setConfirmMessage(
                                              'Remove this document submission? It will be rejected and deleted (same as Onboarding workflow).'
                                            );
                                            setConfirmAction(
                                              () => () =>
                                                reviewOnboardingDocumentSubmission(item.onboardingSubmissionId, 'rejected')
                                            );
                                            setShowConfirmModal(true);
                                          }}
                                        >
                                          <i className="bi bi-trash-fill"></i>
                                        </Button>
                                      ) : (
                                        <DropdownButton
                                          id={`dropdown-onb-sub-${item.id}`}
                                          title="Actions"
                                          size="sm"
                                          variant="outline-secondary"
                                          className="me-2"
                                        >
                                          <Dropdown.Item
                                            onClick={() =>
                                              reviewOnboardingDocumentSubmission(item.onboardingSubmissionId, 'approved')
                                            }
                                          >
                                            Approve
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            onClick={() =>
                                              reviewOnboardingDocumentSubmission(item.onboardingSubmissionId, 'rejected')
                                            }
                                          >
                                            Reject
                                          </Dropdown.Item>
                                        </DropdownButton>
                                      )
                                    ) : item.status === 'approved' ? (
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => openDeleteEvidenceConfirm(item)}
                                      >
                                        <i className="bi bi-trash-fill"></i>
                                      </Button>
                                    ) : (
                                      <>
                                        <DropdownButton
                                          id={`dropdown-evidence-actions-${item.id}`}
                                          title="Actions"
                                          size="sm"
                                          variant="outline-secondary"
                                          className="me-2"
                                        >
                                          <Dropdown.Item onClick={() => reviewEvidence(item.id, 'approved')}>
                                            Approve
                                          </Dropdown.Item>
                                          {item.status === 'rejected' ? (
                                            <Dropdown.Item onClick={() => openDeleteEvidenceConfirm(item)}>
                                              Remove
                                            </Dropdown.Item>
                                          ) : (
                                            <Dropdown.Item onClick={() => requestRejectEvidence(item)}>
                                              Reject
                                            </Dropdown.Item>
                                          )}
                                        </DropdownButton>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </Card.Body>
                    </Card>
                  </Tab.Pane>
                  
              {/* Onboarding Requirements Review */}
              <Tab.Pane eventKey="onboarding" key="onboarding">
                    <Card className="shadow-sm border-0 mb-3" style={{ height: '100%', minHeight: '425px' }}>
                      {renderChecklistHeader('Onboarding Requirements Review')}
                      <Card.Body className={evidenceReviewRowsOnboarding.length === 0 ? 'p-4' : 'p-0'}>
                        {evidenceReviewRowsOnboarding.length === 0 ? (
                          <div className="text-center py-5 px-3 text-muted">
                            <i className="bi bi-inbox-fill mb-3" style={{ fontSize: '2.75rem' }} />
                            <div className="fw-semibold text-muted mb-1">Nothing to review yet</div>
                            <p className="small mb-0 mx-auto" style={{ maxWidth: 420 }}>
                              When residents submit onboarding requirements for your program, they will appear here for approval.
                            </p>
                          </div>
                        ) : (
                          <Table responsive className="mb-0">
                            <thead>
                              <tr className="custom-header-bg">
                                <th className="px-4">Resident</th>
                                <th>Item</th>
                                <th>Submitted details</th>
                                <th>Status</th>
                                
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {evidenceReviewRowsOnboarding.map((item) => (
                                <tr key={item.id}>
                                  <td className="px-4">
                                    {item.resident?.firstName} {item.resident?.lastName}
                                  </td>
                                  <td>
                                    <div className="d-flex flex-column gap-1">
                                      <span>{item.itemName}</span>
                                    </div>
                                  </td>
                                  <td>
                                    {item.isOnboardingBridge && item.bridgeKind === 'package_assignment' ? (
                                      <span className="small text-muted">Structured package — open in Onboarding to review sections.</span>
                                    ) : item.category === 'vaccinationCard' ? null : (
                                      renderEvidenceSubmittedDetailsCell('onboardingRequirements', item)
                                    )}
                                  </td>
                                  <td>
                                    <Badge
                                      bg={
                                        item.status === 'approved'
                                          ? 'success'
                                          : item.status === 'rejected'
                                            ? 'danger'
                                            : 'warning'
                                      }
                                    >
                                      {item.status}
                                    </Badge>
                                  </td>
                                  <td>
                                    {item.isOnboardingBridge && item.bridgeKind === 'package_assignment' ? (
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => {
                                          const rid = item.resident?.id;
                                          window.location.href = rid
                                            ? `/onboarding#tab=submissions&resident=${rid}`
                                            : '/onboarding#tab=submissions';
                                        }}
                                      >
                                        Open in Onboarding
                                      </Button>
                                    ) : item.isOnboardingBridge && item.bridgeKind === 'document_submission' ? (
                                      item.status === 'approved' ? (
                                        <Button
                                          size="sm"
                                          variant="outline-danger"
                                          onClick={() => {
                                            setConfirmMessage(
                                              'Remove this document submission? It will be rejected and deleted (same as Onboarding workflow).'
                                            );
                                            setConfirmAction(
                                              () => () =>
                                                reviewOnboardingDocumentSubmission(item.onboardingSubmissionId, 'rejected')
                                            );
                                            setShowConfirmModal(true);
                                          }}
                                        >
                                          <i className="bi bi-trash-fill"></i>
                                        </Button>
                                      ) : (
                                        <DropdownButton
                                          id={`dropdown-onb-sub-${item.id}`}
                                          title="Actions"
                                          size="sm"
                                          variant="outline-secondary"
                                          className="me-2"
                                        >
                                          <Dropdown.Item
                                            onClick={() =>
                                              reviewOnboardingDocumentSubmission(item.onboardingSubmissionId, 'approved')
                                            }
                                          >
                                            Approve
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            onClick={() =>
                                              reviewOnboardingDocumentSubmission(item.onboardingSubmissionId, 'rejected')
                                            }
                                          >
                                            Reject
                                          </Dropdown.Item>
                                        </DropdownButton>
                                      )
                                    ) : item.status === 'approved' ? (
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => openDeleteEvidenceConfirm(item)}
                                      >
                                        <i className="bi bi-trash-fill"></i>
                                      </Button>
                                    ) : (
                                      <>
                                        <DropdownButton
                                          id={`dropdown-evidence-actions-${item.id}`}
                                          title="Actions"
                                          size="sm"
                                          variant="outline-secondary"
                                          className="me-2"
                                        >
                                          <Dropdown.Item onClick={() => reviewEvidence(item.id, 'approved')}>
                                            Approve
                                          </Dropdown.Item>
                                          {item.status === 'rejected' ? (
                                            <Dropdown.Item onClick={() => openDeleteEvidenceConfirm(item)}>
                                              Remove
                                            </Dropdown.Item>
                                          ) : (
                                            <Dropdown.Item onClick={() => requestRejectEvidence(item)}>
                                              Reject
                                            </Dropdown.Item>
                                          )}
                                        </DropdownButton>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </Card.Body>
                    </Card>
                  </Tab.Pane>
              
              {/* Training Certifications Review */}
              <Tab.Pane eventKey="training" key="training">
                    <Card className="shadow-sm border-0 mb-3" style={{ height: '100%', minHeight: '425px' }}>
                      {renderChecklistHeader('Training Certifications Review')}
                      <Card.Body className={evidenceReviewRowsTraining.length === 0 ? 'p-4' : 'p-0'}>
                        {evidenceReviewRowsTraining.length === 0 ? (
                          <div className="text-center py-5 px-3 text-muted">
                            <i className="bi bi-inbox-fill mb-3" style={{ fontSize: '2.75rem' }} />
                            <div className="fw-semibold text-muted mb-1">Nothing to review yet</div>
                            <p className="small mb-0 mx-auto" style={{ maxWidth: 420 }}>
                              When residents submit training certifications for your program, they will appear here for approval.
                            </p>
                          </div>
                        ) : (
                          <Table responsive className="mb-0">
                            <thead>
                              <tr className="custom-header-bg">
                                <th className="px-4">Resident</th>
                                <th>Item</th>

                                <th>Submitted details</th>
                                <th>Status</th>
                                <th>Evidence</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {evidenceReviewRowsTraining.map((item) => (
                                <tr key={item.id}>
                                  <td className="px-4">
                                    {item.resident?.firstName} {item.resident?.lastName}
                                  </td>
                                  <td>
                                    <div className="d-flex flex-column gap-1">
                                      <span>{item.itemName}</span>
                                    </div>
                                  </td>
                                  <td>
                                    {item.isOnboardingBridge && item.bridgeKind === 'package_assignment' ? (
                                      <span className="small text-muted">Structured package — open in Onboarding to review sections.</span>
                                    ) : item.category === 'vaccinationCard' ? null : (
                                      renderEvidenceSubmittedDetailsCell('trainingCertifications', item)
                                    )}
                                  </td>
                                  <td>
                                    <Badge
                                      bg={
                                        item.status === 'approved'
                                          ? 'success'
                                          : item.status === 'rejected'
                                            ? 'danger'
                                            : 'warning'
                                      }
                                    >
                                      {item.status}
                                    </Badge>
                                  </td>
                                  <td>
                                    {item.evidencePath ? (
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => openProtectedEvidenceFile(item.evidencePath)}
                                      >
                                        View
                                      </Button>
                                    ) : item.category === 'vaccinationCard' ? (
                                      <Button
                                        size="sm"
                                        variant="primary"
                                        onClick={() => setVaccinationCardViewItem(item)}
                                      >
                                        <i className="bi bi-card-checklist me-1" />
                                         vaccination card
                                      </Button>
                                    ) : item.isOnboardingBridge && item.bridgeKind === 'package_assignment' ? (
                                      <span className="small text-muted">—</span>
                                    ) : (
                                      'N/A'
                                    )}
                                  </td>
                                  <td>
                                    {item.isOnboardingBridge && item.bridgeKind === 'package_assignment' ? (
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => {
                                          const rid = item.resident?.id;
                                          window.location.href = rid
                                            ? `/onboarding#tab=submissions&resident=${rid}`
                                            : '/onboarding#tab=submissions';
                                        }}
                                      >
                                        Open in Onboarding
                                      </Button>
                                    ) : item.isOnboardingBridge && item.bridgeKind === 'document_submission' ? (
                                      item.status === 'approved' ? (
                                        <Button
                                          size="sm"
                                          variant="outline-danger"
                                          onClick={() => {
                                            setConfirmMessage(
                                              'Remove this document submission? It will be rejected and deleted (same as Onboarding workflow).'
                                            );
                                            setConfirmAction(
                                              () => () =>
                                                reviewOnboardingDocumentSubmission(item.onboardingSubmissionId, 'rejected')
                                            );
                                            setShowConfirmModal(true);
                                          }}
                                        >
                                          <i className="bi bi-trash-fill"></i>
                                        </Button>
                                      ) : (
                                        <DropdownButton
                                          id={`dropdown-onb-sub-${item.id}`}
                                          title="Actions"
                                          size="sm"
                                          variant="outline-secondary"
                                          className="me-2"
                                        >
                                          <Dropdown.Item
                                            onClick={() =>
                                              reviewOnboardingDocumentSubmission(item.onboardingSubmissionId, 'approved')
                                            }
                                          >
                                            Approve
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            onClick={() =>
                                              reviewOnboardingDocumentSubmission(item.onboardingSubmissionId, 'rejected')
                                            }
                                          >
                                            Reject
                                          </Dropdown.Item>
                                        </DropdownButton>
                                      )
                                    ) : item.status === 'approved' ? (
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => openDeleteEvidenceConfirm(item)}
                                      >
                                        <i className="bi bi-trash-fill"></i>
                                      </Button>
                                    ) : (
                                      <>
                                        <DropdownButton
                                          id={`dropdown-evidence-actions-${item.id}`}
                                          title="Actions"
                                          size="sm"
                                          variant="outline-secondary"
                                          className="me-2"
                                        >
                                          <Dropdown.Item onClick={() => reviewEvidence(item.id, 'approved')}>
                                            Approve
                                          </Dropdown.Item>
                                          {item.status === 'rejected' ? (
                                            <Dropdown.Item onClick={() => openDeleteEvidenceConfirm(item)}>
                                              Remove
                                            </Dropdown.Item>
                                          ) : (
                                            <Dropdown.Item onClick={() => requestRejectEvidence(item)}>
                                              Reject
                                            </Dropdown.Item>
                                          )}
                                        </DropdownButton>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </Card.Body>
                    </Card>
                  </Tab.Pane>
              
              {/* Vaccinations Review */}
              <Tab.Pane eventKey="vaccinations" key="vaccinations">
                    <Card className="shadow-sm border-0 mb-3" style={{ height: '100%', minHeight: '425px' }}>
                      {renderChecklistHeader('Vaccinations Review')}
                      <Card.Body className={evidenceReviewRowsVaccinations.length === 0 ? 'p-4' : 'p-0'}>
                        {evidenceReviewRowsVaccinations.length === 0 ? (
                          <div className="text-center py-5 px-3 text-muted">
                            <i className="bi bi-inbox-fill mb-3" style={{ fontSize: '2.75rem' }} />
                            <div className="fw-semibold text-muted mb-1">Nothing to review yet</div>
                            <p className="small mb-0 mx-auto" style={{ maxWidth: 420 }}>
                              When residents submit vaccinations for your program, they will appear here for approval.
                            </p>
                          </div>
                        ) : (
                          <Table responsive className="mb-0">
                            <thead>
                              <tr className="custom-header-bg">
                                <th className="px-4">Resident</th>
                                <th>Item</th>
                                <th>Status</th>
                                <th>Evidence</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {buildVaccinationDisplayEntries(evidenceReviewRowsVaccinations).map((entry) =>
                                entry.kind === 'vaxHeader' ? (
                                  <tr
                                    key={entry.key}
                                    className={`vaccination-review-accordion-trigger${
                                      vaccinationSubmissionsExpandedIds.has(String(entry.residentId))
                                        ? ' vaccination-review-accordion-trigger-expanded'
                                        : ''
                                    }`}
                                    onClick={(e) => {
                                      if (e.target.closest('button, a, [role="menuitem"], .dropdown, input')) return;
                                      toggleVaccinationSubmissionsPanel(entry.residentId);
                                    }}
                                  >
                                    <td
                                      className="px-4"
                                      tabIndex={0}
                                      aria-expanded={vaccinationSubmissionsExpandedIds.has(String(entry.residentId))}
                                      aria-label={
                                        vaccinationSubmissionsExpandedIds.has(String(entry.residentId))
                                          ? 'Collapse individual vaccination submissions'
                                          : 'Expand individual vaccination submissions'
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key !== 'Enter' && e.key !== ' ') return;
                                        e.preventDefault();
                                        toggleVaccinationSubmissionsPanel(entry.residentId);
                                      }}
                                    >
                                      <div className="d-flex align-items-center gap-2">
                                        <i
                                          className={`bi flex-shrink-0 vaccination-review-accordion-chevron ${
                                            vaccinationSubmissionsExpandedIds.has(String(entry.residentId))
                                              ? 'bi-chevron-up'
                                              : 'bi-chevron-down'
                                          }`}
                                          aria-hidden
                                        />
                                        <div>
                                          <div className="fw-semibold">
                                            {entry.resident?.firstName} {entry.resident?.lastName}
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="fw-semibold">Individual vaccinations</td>
                                    <td className="text-muted small">—</td>
                                    <td className="text-muted small">—</td>
                                    <td className="text-muted small">—</td>
                                  </tr>
                                ) : entry.kind === 'vaxNest' ? (
                                  vaccinationSubmissionsExpandedIds.has(String(entry.residentId)) ? (
                                  <tr key={entry.key} >
                                    <td colSpan={5} className="p-0 border-0">
                                      <div className="bg-light p-2 border-bottom">
                                        <Table
                                          responsive
                                          size="sm"
                                          className="mb-0"
                                        >
                                          <thead>
                                            <tr>
                                              <th className="text-nowrap">Item</th>
                                              <th>Submitted details</th>
                                              <th className="text-nowrap">Status</th>
                                              <th className="text-nowrap">Evidence</th>
                                              <th className="text-nowrap">Actions</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {entry.items.map((item) => (
                                              <tr key={item.id}>
                                                <td className="fw-semibold text-nowrap">{item.itemName}</td>
                                                <td className="small ">
                                                  {renderEvidenceSubmittedDetailsCell('vaccinations', item)}
                                                </td>
                                                <td>
                                                  <Badge
                                                    bg={
                                                      item.status === 'approved'
                                                        ? 'success'
                                                        : item.status === 'rejected'
                                                          ? 'danger'
                                                          : 'warning'
                                                    }
                                                  >
                                                    {item.status}
                                                  </Badge>
                                                </td>
                                                <td>
                                                  {item.evidencePath ? (
                                                    <Button
                                                      size="sm"
                                                      variant="outline-primary"
                                                      onClick={() => openProtectedEvidenceFile(item.evidencePath)}
                                                    >
                                                      View
                                                    </Button>
                                                  ) : (
                                                    <span className="text-muted small">N/A</span>
                                                  )}
                                                </td>
                                                <td>
                                                  {item.status === 'approved' ? (
                                                    <Button
                                                      size="sm"
                                                      variant="outline-danger"
                                                      onClick={() => openDeleteEvidenceConfirm(item)}
                                                    >
                                                      <i className="bi bi-trash-fill"></i>
                                                    </Button>
                                                  ) : (
                                                    <DropdownButton
                                                      id={`dropdown-evidence-actions-${item.id}`}
                                                      title="Actions"
                                                      size="sm"
                                                      variant="outline-secondary"
                                                      className="me-2"
                                                    >
                                                      <Dropdown.Item onClick={() => reviewEvidence(item.id, 'approved')}>
                                                        Approve
                                                      </Dropdown.Item>
                                                      {item.status === 'rejected' ? (
                                                        <Dropdown.Item onClick={() => openDeleteEvidenceConfirm(item)}>
                                                          Remove
                                                        </Dropdown.Item>
                                                      ) : (
                                                        <Dropdown.Item onClick={() => requestRejectEvidence(item)}>
                                                          Reject
                                                        </Dropdown.Item>
                                                      )}
                                                    </DropdownButton>
                                                  )}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </Table>
                                      </div>
                                    </td>
                                  </tr>
                                  ) : null
                                ) : (
                                <tr
                                  key={entry.item.id}
                                  className={
                                    entry.item.category === 'vaccinationCard' &&
                                    entry.showVaxSubmissionsToggle
                                      ? `vaccination-review-accordion-trigger${
                                          vaccinationSubmissionsExpandedIds.has(
                                            String(entry.vaccinationGroupResidentId)
                                          )
                                            ? ' vaccination-review-accordion-trigger-expanded'
                                            : ''
                                        }`
                                      : undefined
                                  }
                                  onClick={
                                    entry.item.category === 'vaccinationCard' &&
                                    entry.showVaxSubmissionsToggle
                                      ? (e) => {
                                          if (
                                            e.target.closest(
                                              'button, a, [role="menuitem"], .dropdown, input, select, textarea'
                                            )
                                          )
                                            return;
                                          toggleVaccinationSubmissionsPanel(entry.vaccinationGroupResidentId);
                                        }
                                      : undefined
                                  }
                                >
                                  <td
                                    className="px-4"
                                    tabIndex={
                                      entry.item.category === 'vaccinationCard' &&
                                      entry.showVaxSubmissionsToggle
                                        ? 0
                                        : undefined
                                    }
                                    aria-expanded={
                                      entry.item.category === 'vaccinationCard' &&
                                      entry.showVaxSubmissionsToggle
                                        ? vaccinationSubmissionsExpandedIds.has(
                                            String(entry.vaccinationGroupResidentId)
                                          )
                                        : undefined
                                    }
                                    aria-label={
                                      entry.item.category === 'vaccinationCard' &&
                                      entry.showVaxSubmissionsToggle
                                        ? vaccinationSubmissionsExpandedIds.has(
                                            String(entry.vaccinationGroupResidentId)
                                          )
                                          ? 'Collapse individual vaccination submissions'
                                          : 'Expand individual vaccination submissions'
                                        : undefined
                                    }
                                    onKeyDown={
                                      entry.item.category === 'vaccinationCard' &&
                                      entry.showVaxSubmissionsToggle
                                        ? (e) => {
                                            if (e.key !== 'Enter' && e.key !== ' ') return;
                                            e.preventDefault();
                                            toggleVaccinationSubmissionsPanel(entry.vaccinationGroupResidentId);
                                          }
                                        : undefined
                                    }
                                  >
                                    {entry.item.category === 'vaccinationCard' &&
                                    entry.showVaxSubmissionsToggle ? (
                                      <div className="d-flex align-items-center gap-2">
                                        {vaccinationSubmissionsExpandedIds.has(String(entry.vaccinationGroupResidentId)) ? (
                                          <ChevronUp
                                            className="flex-shrink-0 vaccination-review-accordion-chevron"
                                            aria-hidden="true"
                                            size={18}
                                          />
                                        ) : (
                                          <ChevronDown
                                            className="flex-shrink-0 vaccination-review-accordion-chevron"
                                            aria-hidden="true"
                                            size={18}
                                          />
                                        )}
                                 
                                        <span>
                                          {entry.item.resident?.firstName} {entry.item.resident?.lastName}
                                        </span>
                                      </div>
                                    ) : (
                                      <>
                                        {entry.item.resident?.firstName} {entry.item.resident?.lastName}
                                      </>
                                    )}
                                  </td>
                                  <td>
                                    <div className="d-flex flex-column gap-1">
                                      <span>{entry.item.itemName}</span>
                                      {entry.item.category === 'vaccinationCard' &&
                                      entry.showVaxSubmissionsToggle ? (
                                        <span className="small text-muted fw-normal">
                                          {entry.vaxSubmissionsCount} additional submission
                                         {/* {entry.vaxSubmissionsCount === 1 ? '' : 's'} — click row to expand */}
                                        </span>
                                      ) : null}
                                      {entry.item.category !== 'vaccinationCard' ? (
                                        entry.item.isOnboardingBridge && entry.item.bridgeKind === 'package_assignment' ? (
                                          <span className="small text-muted">Structured package — open in Onboarding to review sections.</span>
                                        ) : (
                                          <div className="small">{renderEvidenceSubmittedDetailsCell('vaccinations', entry.item)}</div>
                                        )
                                      ) : entry.item.isOnboardingBridge && entry.item.bridgeKind === 'package_assignment' ? (
                                        <span className="small text-muted">Structured package — open in Onboarding to review sections.</span>
                                      ) : null}
                                    </div>
                                  </td>
                                  <td>
                                    <Badge
                                      bg={
                                        entry.item.status === 'approved'
                                          ? 'success'
                                          : entry.item.status === 'rejected'
                                            ? 'danger'
                                            : 'warning'
                                      }
                                    >
                                      {entry.item.status}
                                    </Badge>
                                  </td>
                                  <td>
                                    {entry.item.evidencePath ? (
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => openProtectedEvidenceFile(entry.item.evidencePath)}
                                      >
                                        View
                                      </Button>
                                    ) : entry.item.category === 'vaccinationCard' ? (
                                      <Button
                                        size="sm"
                                        variant="primary"
                                        onClick={() => setVaccinationCardViewItem(entry.item)}
                                      >
                                        <i className="bi bi-card-checklist me-1" />
                                        vaccination card
                                      </Button>
                                    ) : entry.item.isOnboardingBridge && entry.item.bridgeKind === 'package_assignment' ? (
                                      <span className="small text-muted">—</span>
                                    ) : (
                                      'N/A'
                                    )}
                                  </td>
                                  <td>
                                    {entry.item.isOnboardingBridge && entry.item.bridgeKind === 'package_assignment' ? (
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => {
                                          const rid = entry.item.resident?.id;
                                          window.location.href = rid
                                            ? `/onboarding#tab=submissions&resident=${rid}`
                                            : '/onboarding#tab=submissions';
                                        }}
                                      >
                                        Open in Onboarding
                                      </Button>
                                    ) : entry.item.isOnboardingBridge && entry.item.bridgeKind === 'document_submission' ? (
                                      entry.item.status === 'approved' ? (
                                        <Button
                                          size="sm"
                                          variant="outline-danger"
                                          onClick={() => {
                                            setConfirmMessage(
                                              'Remove this document submission? It will be rejected and deleted (same as Onboarding workflow).'
                                            );
                                            setConfirmAction(
                                              () => () =>
                                                reviewOnboardingDocumentSubmission(entry.item.onboardingSubmissionId, 'rejected')
                                            );
                                            setShowConfirmModal(true);
                                          }}
                                        >
                                          <i className="bi bi-trash-fill"></i>
                                        </Button>
                                      ) : (
                                        <DropdownButton
                                          id={`dropdown-onb-sub-${entry.item.id}`}
                                          title="Actions"
                                          size="sm"
                                          variant="outline-secondary"
                                          className="me-2"
                                        >
                                          <Dropdown.Item
                                            onClick={() =>
                                              reviewOnboardingDocumentSubmission(entry.item.onboardingSubmissionId, 'approved')
                                            }
                                          >
                                            Approve
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            onClick={() =>
                                              reviewOnboardingDocumentSubmission(entry.item.onboardingSubmissionId, 'rejected')
                                            }
                                          >
                                            Reject
                                          </Dropdown.Item>
                                        </DropdownButton>
                                      )
                                    ) : entry.item.status === 'approved' ? (
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => openDeleteEvidenceConfirm(entry.item)}
                                      >
                                        <i className="bi bi-trash-fill"></i>
                                      </Button>
                                    ) : (
                                      <>
                                        <DropdownButton
                                          id={`dropdown-evidence-actions-${entry.item.id}`}
                                          title="Actions"
                                          size="sm"
                                          variant="outline-secondary"
                                          className="me-2"
                                        >
                                          <Dropdown.Item onClick={() => reviewEvidence(entry.item.id, 'approved')}>
                                            Approve
                                          </Dropdown.Item>
                                          {entry.item.status === 'rejected' ? (
                                            <Dropdown.Item onClick={() => openDeleteEvidenceConfirm(entry.item)}>
                                              Remove
                                            </Dropdown.Item>
                                          ) : (
                                            <Dropdown.Item onClick={() => requestRejectEvidence(entry.item)}>
                                              Reject
                                            </Dropdown.Item>
                                          )}
                                        </DropdownButton>
                                      </>
                                    )}
                                  </td>
                                </tr>
                                )
                              )}
                            </tbody>
                          </Table>
                        )}
                      </Card.Body>
                    </Card>
                  </Tab.Pane>
              
              {/* Scholarly Activity Review */}
              <Tab.Pane eventKey="scholarly" key="scholarly">
                    <Card className="shadow-sm border-0 mb-3" style={{ height: '100%', minHeight: '425px' }}>
                      {renderChecklistHeader('Scholarly Activity Review')}
                      <Card.Body className={evidenceReviewRowsScholarly.length === 0 ? 'p-4' : 'p-0'}>
                        {evidenceReviewRowsScholarly.length === 0 ? (
                          <div className="text-center py-5 px-3 text-muted">
                            <i className="bi bi-inbox-fill mb-3" style={{ fontSize: '2.75rem' }} />
                            <div className="fw-semibold text-muted mb-1">Nothing to review yet</div>
                            <p className="small mb-0 mx-auto" style={{ maxWidth: 420 }}>
                              When residents submit scholarly activity for your program, they will appear here for approval.
                            </p>
                          </div>
                        ) : (
                          <Table responsive className="mb-0">
                            <thead>
                              <tr className="custom-header-bg">
                                <th className="px-4">Resident</th>
                                <th>Item</th>
                                <th>Submitted details</th>
                                <th>Status</th>
                                <th>Evidence</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {evidenceReviewRowsScholarly.map((item) => (
                                <tr key={item.id}>
                                  <td className="px-4">
                                    {item.resident?.firstName} {item.resident?.lastName}
                                  </td>
                                  <td>
                                    <div className="d-flex flex-column gap-1">
                                      <span>{item.itemName}</span>
                                    </div>
                                  </td>
                                  <td>
                                    {item.isOnboardingBridge && item.bridgeKind === 'package_assignment' ? (
                                      <span className="small text-muted">Structured package — open in Onboarding to review sections.</span>
                                    ) : item.category === 'vaccinationCard' ? null : (
                                      renderEvidenceSubmittedDetailsCell('scholarlyActivity', item)
                                    )}
                                  </td>
                                  <td>
                                    <Badge
                                      bg={
                                        item.status === 'approved'
                                          ? 'success'
                                          : item.status === 'rejected'
                                            ? 'danger'
                                            : 'warning'
                                      }
                                    >
                                      {item.status}
                                    </Badge>
                                  </td>
                                  <td>
                                    {item.evidencePath ? (
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => openProtectedEvidenceFile(item.evidencePath)}
                                      >
                                        View
                                      </Button>
                                    ) : item.category === 'vaccinationCard' ? (
                                      <Button
                                        size="sm"
                                        variant="primary"
                                        onClick={() => setVaccinationCardViewItem(item)}
                                      >
                                        <i className="bi bi-card-checklist me-1" />
                                        vaccination card
                                      </Button>
                                    ) : item.isOnboardingBridge && item.bridgeKind === 'package_assignment' ? (
                                      <span className="small text-muted">—</span>
                                    ) : (
                                      'N/A'
                                    )}
                                  </td>
                                  <td>
                                    {item.isOnboardingBridge && item.bridgeKind === 'package_assignment' ? (
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => {
                                          const rid = item.resident?.id;
                                          window.location.href = rid
                                            ? `/onboarding#tab=submissions&resident=${rid}`
                                            : '/onboarding#tab=submissions';
                                        }}
                                      >
                                        Open in Onboarding
                                      </Button>
                                    ) : item.isOnboardingBridge && item.bridgeKind === 'document_submission' ? (
                                      item.status === 'approved' ? (
                                        <Button
                                          size="sm"
                                          variant="outline-danger"
                                          onClick={() => {
                                            setConfirmMessage(
                                              'Remove this document submission? It will be rejected and deleted (same as Onboarding workflow).'
                                            );
                                            setConfirmAction(
                                              () => () =>
                                                reviewOnboardingDocumentSubmission(item.onboardingSubmissionId, 'rejected')
                                            );
                                            setShowConfirmModal(true);
                                          }}
                                        >
                                          <i className="bi bi-trash-fill"></i>
                                        </Button>
                                      ) : (
                                        <DropdownButton
                                          id={`dropdown-onb-sub-${item.id}`}
                                          title="Actions"
                                          size="sm"
                                          variant="outline-secondary"
                                          className="me-2"
                                        >
                                          <Dropdown.Item
                                            onClick={() =>
                                              reviewOnboardingDocumentSubmission(item.onboardingSubmissionId, 'approved')
                                            }
                                          >
                                            Approve
                                          </Dropdown.Item>
                                          <Dropdown.Item
                                            onClick={() =>
                                              reviewOnboardingDocumentSubmission(item.onboardingSubmissionId, 'rejected')
                                            }
                                          >
                                            Reject
                                          </Dropdown.Item>
                                        </DropdownButton>
                                      )
                                    ) : item.status === 'approved' ? (
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => openDeleteEvidenceConfirm(item)}
                                      >
                                        <i className="bi bi-trash-fill"></i>
                                      </Button>
                                    ) : (
                                      <>
                                        <DropdownButton
                                          id={`dropdown-evidence-actions-${item.id}`}
                                          title="Actions"
                                          size="sm"
                                          variant="outline-secondary"
                                          className="me-2"
                                        >
                                          <Dropdown.Item onClick={() => reviewEvidence(item.id, 'approved')}>
                                            Approve
                                          </Dropdown.Item>
                                          {item.status === 'rejected' ? (
                                            <Dropdown.Item onClick={() => openDeleteEvidenceConfirm(item)}>
                                              Remove
                                            </Dropdown.Item>
                                          ) : (
                                            <Dropdown.Item onClick={() => requestRejectEvidence(item)}>
                                              Reject
                                            </Dropdown.Item>
                                          )}
                                        </DropdownButton>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </Card.Body>
                    </Card>
                  </Tab.Pane>
    </>
  );
}
